import { DropdownButton } from "@/components/ui/button";
import { Menu, MenuItem, MenuPopover, MenuTrigger } from "@/components/ui/menu";
import type { FeedArticleProjectionOrdering } from "@/data-access/shared/types/feed-article-projection.types";
import { createOrdering } from "@/lib/utils/articleSortOrderingUtils";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import { SortAscendingIcon, SortDescendingIcon } from "@phosphor-icons/react";

type SortOption = {
  shortLabel: string;
  label: string;
  icon: React.ReactNode;
  ordering: FeedArticleProjectionOrdering;
};

const sortOptions: SortOption[] = [
  {
    shortLabel: "Score",
    label: "Score: Highest to Lowest",
    icon: <SortAscendingIcon className="w-5 h-5" />,
    ordering: createOrdering("impactScore", "desc"),
  },
  {
    shortLabel: "Score",
    label: "Score: Lowest to Highest",
    icon: <SortDescendingIcon className="w-5 h-5" />,
    ordering: createOrdering("impactScore", "asc"),
  },
  {
    shortLabel: "Publication Date",
    label: "Publish Date: Newest to Oldest",
    icon: <SortAscendingIcon className="w-5 h-5" />,
    ordering: createOrdering("datePublished", "desc"),
  },
  {
    shortLabel: "Publication Date",
    label: "Publish Date: Oldest to Newest",
    icon: <SortDescendingIcon className="w-5 h-5" />,
    ordering: createOrdering("datePublished", "asc"),
  },
];

const ArticleSortController = () => {
  const ordering = useFeedContentManagementStore((state) => state.ordering);
  const setOrdering = useFeedContentManagementStore(
    (state) => state.setOrdering,
  );

  // Since each option has a single-field ordering, we can compare directly
  const selectedSortOption = sortOptions.find((option) => {
    const optionOrder = option.ordering[0];
    const currentOrder = ordering[0];
    if (!optionOrder || !currentOrder) return false;
    // Compare the single field and its value
    const optionKey = Object.keys(optionOrder)[0] as keyof typeof optionOrder;
    const currentKey = Object.keys(
      currentOrder,
    )[0] as keyof typeof currentOrder;
    return (
      optionKey === currentKey &&
      optionOrder[optionKey] === currentOrder[currentKey]
    );
  });

  const handleSortChange = (option: SortOption) => {
    setOrdering(option.ordering);
  };

  return (
    <MenuTrigger>
      <DropdownButton
        ariaLabel="Sort Articles By Options"
        icon={selectedSortOption?.icon}
        label={selectedSortOption?.shortLabel || "Sort"}
      />
      <MenuPopover>
        <Menu className="bg-white">
          {sortOptions.map((option) => (
            <MenuItem
              key={option.label}
              onAction={() => handleSortChange(option)}
            >
              {option.icon}
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </MenuPopover>
    </MenuTrigger>
  );
};

export default ArticleSortController;
